import { Box } from 'lucide-react';
import { Button } from '../../../ui/button';
import { Checkbox } from '../../../ui/checkbox';
import { DialogBody, DialogFooter } from '../../../ui/dialog';
import { ProductImage } from '../../../ui/product-image';
import { cn } from '../../../../lib/utils';
import type { QuoteItem } from './types';

interface WizardProductsStepProps {
	quoteItems: QuoteItem[];
	selectedItems: Set<string>;
	toggleItem: (id: string) => void;
	existingOfferItemIds: string[];
	onBack: () => void;
	onNext: () => void;
	onCancel: () => void;
	/** True when the wizard is adding to an existing batch (back = cancel) */
	isAddMode: boolean;
}

export function WizardProductsStep({
	quoteItems,
	selectedItems,
	toggleItem,
	existingOfferItemIds,
	onBack,
	onNext,
	onCancel,
	isAddMode,
}: WizardProductsStepProps) {
	return (
		<div
			key="products"
			className="flex min-h-0 flex-1 animate-in fade-in-0 slide-in-from-right-2 flex-col duration-200"
		>
			<DialogBody className="px-3 py-2">
				<div className="flex flex-col gap-1">
					{quoteItems
						.filter((item) => !existingOfferItemIds.includes(item.id))
						.map((item) => {
							const isSelected = selectedItems.has(item.id);
							const primaryImg =
								item.productImages?.find((i) => i.isPrimary) ??
								item.productImages?.[0] ??
								null;
							return (
								<button
									key={item.id}
									onClick={() => toggleItem(item.id)}
									className={cn(
										'flex w-full cursor-pointer items-center gap-3.5 rounded-xl px-3 py-3 text-left transition-colors hover:bg-surface-hover',
										isSelected && 'bg-surface-fill'
									)}
								>
									<Checkbox
										checked={isSelected}
										tabIndex={-1}
										className="pointer-events-none"
									/>
									{primaryImg?.url ? (
										<ProductImage
											image={primaryImg}
											alt={item.productName || ''}
											className="h-14 w-14 shrink-0 rounded-lg border border-line-1"
										/>
									) : (
										<div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-lg border border-line-1 bg-surface-fill">
											<Box size={18} className="text-text-4" />
										</div>
									)}
									<div className="min-w-0 flex-1">
										<div className="text-base font-medium text-text-1">
											{item.productName || 'Unknown'}
										</div>
										{item.productSku && (
											<div className="mt-0.5 font-mono text-xs text-text-4">
												{item.productSku}
											</div>
										)}
										{item.quantities.length > 0 && (
											<div className="mt-1 flex gap-1">
												{item.quantities.map((q) => (
													<span
														key={q.id}
														className="inline-flex rounded border border-line-2 bg-surface-fill px-1.5 py-0.5 font-mono text-2xs text-text-3"
													>
														{q.quantity.toLocaleString()}
													</span>
												))}
											</div>
										)}
									</div>
								</button>
							);
						})}
				</div>
			</DialogBody>
			<DialogFooter>
				<Button variant="outline" onClick={isAddMode ? onCancel : onBack}>
					{isAddMode ? 'Cancel' : 'Back'}
				</Button>
				<Button onClick={onNext} disabled={selectedItems.size === 0}>
					Next ({selectedItems.size})
				</Button>
			</DialogFooter>
		</div>
	);
}
